import { useCallback } from 'react';
import { useNovelRepository } from '@/hooks/useNovelRepository';
import type { EntitySummary } from '@/lib/types';
import { getNextRelationshipGraphPosition } from '@/lib/relationship-graph-layout';
import { useEntityAccessLayer } from '../../shared/hooks/use-entity-access-layer';
import { mapCodexDTOToDomain } from '../api/mappers';
import type { CodexFormValues } from '../schemas';
import type { Codex } from '../types';

/**
 * Fields on the Codex entry that are mirrored into the EntitySummary.
 * When any of these change, the summary is automatically updated.
 */
const CODEX_SUMMARY_FIELDS = [
  'name',
  'type',
  'shortDescription',
  'mainImageUrl',
  'mainImagePath',
  'graphPosition',
] as const;

/**
 * Centralised access layer for codex entries.
 */
export function useCodexAccessLayer(entryId?: string, category?: string) {
  const settingsKey = category ? `codex:${category}` : 'codex';
  const {
    summaries,
    item,
    sections,
    isLoading,
    error,
    updateItem,
    deleteItem,
    getNewId,
  } = useEntityAccessLayer<Codex, EntitySummary>({
    entityType: 'codex',
    settingsType: settingsKey,
    summaryFieldKeys: CODEX_SUMMARY_FIELDS,
    itemId: entryId,
    itemMapper: mapCodexDTOToDomain,
  });

  const {
    novel,
    saveItem,
    updateEntitySummary,
  } = useNovelRepository();

  /**
   * Creates a new codex entry with initial data + summary.
   */
  const createEntry = useCallback(
    async (id: string, data: Omit<CodexFormValues, 'pendingFiles'>) => {
      const now = new Date().toISOString();
      const graphPosition = getNextRelationshipGraphPosition(novel);
      const newEntry = {
        ...data,
        id,
        audit: { createdAt: now, updatedAt: now },
        graphPosition,
      };

      // Persist item
      await saveItem('entities', 'codex', newEntry);

      // Extract summary fields
      const name = String(data.name ?? '');
      const type = String(data.type ?? '');
      const shortDescription = String(data.shortDescription ?? '');
      const mainImageUrl = String(data.mainImageUrl ?? '');
      const mainImagePath = String(data.mainImagePath ?? '');
      const primaryImageId = typeof data.primaryImageId === 'string' ? data.primaryImageId : null;

      // Write summary
      await updateEntitySummary('codex', id, {
        id,
        name,
        type,
        shortDescription,
        mainImageUrl,
        mainImagePath,
        primaryImageId,
        graphPosition,
        updatedAt: now,
      });
    },
    [novel, saveItem, updateEntitySummary],
  );

  return {
    entries: summaries,
    entry: item,
    sections,
    isLoading,
    error,
    updateEntry: updateItem,
    deleteEntry: deleteItem,
    createEntry,
    getNewId,
  };
}
